import { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Layers } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE,
  TEMPLATES_DUPLICATE,
  TEMPLATES_DELETE,
  TEMPLATES_LOCK_LAYER,
  TEMPLATES_UNLOCK_LAYER,
  TEMPLATES_HIDE_LAYER,
  TEMPLATES_SHOW_LAYER,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_TEXT,
} from "../../../i18n/keysTranslations";
import ModalGalleryAssets from "../ModalGalleryAssets";
import { findNodePath } from "../../../features/creativeTemplates/utils/layerTree";
import {
  layerListNoNativeSelectSx,
  preventLayerListNativeSelectStart,
  preventNativeTextSelectOnAdditivePointer,
} from "../../../features/creativeTemplates/editor/canvasLayerSelection";
import SortableNestedLayerList from "../../../features/creativeTemplates/editor/SortableNestedLayerList";

const addButtonSx = {
  color: "#111",
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: "nowrap",
  userSelect: "none",
  border: "none",
  bgcolor: "transparent",
  p: 0,
  fontFamily: "inherit",
  minWidth: 16,
  minHeight: 16,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
};

const InstanceLayersSidebar = ({
  nodes = {},
  nodeLayouts = {},
  items = [],
  selectedID,
  selectedIDs = [],
  expandedIDs,
  onSelectLayer,
  onRenameLayer,
  onToggleExpand,
  onReorderSiblings,
  onAddLayer,
  onCopyLayer,
  onDeleteLayer,
  onToggleLock,
  onToggleVisible,
}) => {
  const { t } = useTranslationApp();
  const [addMenuEl, setAddMenuEl] = useState(null);
  const [isAddingLayer, setIsAddingLayer] = useState(false);
  const [isAssetsGalleryOpen, setIsAssetsGalleryOpen] = useState(false);
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const hasLayers = items.length > 0;
  const selectedParentID =
    findNodePath({ nodes, nodeID: selectedID })?.parentID || null;

  const onCloseAddMenu = () => setAddMenuEl(null);

  const onSelectGalleryItems = async (selectedItems) => {
    const urls = (selectedItems || []).map((item) => item?.url).filter(Boolean);
    if (!urls.length) return;
    setIsAddingLayer(true);
    try {
      await onAddLayer({ type: "image", urls });
    } finally {
      setIsAddingLayer(false);
    }
  };

  return (
    <Box
      onMouseDown={preventNativeTextSelectOnAdditivePointer}
      onSelectStart={preventLayerListNativeSelectStart}
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...layerListNoNativeSelectSx,
      }}
    >
      <Box
        data-layer-list-scroll=""
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowAnchor: "none",
          px: 2,
          py: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.5,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {t(MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE)}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-busy={isAddingLayer}
              disabled={isAddingLayer}
              onClick={(event) => {
                if (isAddingLayer) return;
                setAddMenuEl(event.currentTarget);
              }}
              sx={{
                ...addButtonSx,
                cursor: isAddingLayer ? "default" : "pointer",
                pointerEvents: isAddingLayer ? "none" : "auto",
              }}
            >
              {isAddingLayer ? (
                <CircularProgress size={14} sx={{ color: "#111" }} />
              ) : (
                `+ ${t(MODAL_IMAGE_EDIT_LAYERS_ADD)}`
              )}
            </Box>
          </Box>
          <Menu
            anchorEl={addMenuEl}
            open={Boolean(addMenuEl)}
            onClose={onCloseAddMenu}
          >
            <MenuItem
              onClick={() => {
                onCloseAddMenu();
                onAddLayer({ type: "text" });
              }}
            >
              {t(TEMPLATES_PRODUCE_ADD_TEXT)}
            </MenuItem>
            <MenuItem
              onClick={() => {
                onCloseAddMenu();
                setIsAssetsGalleryOpen(true);
              }}
            >
              {t(TEMPLATES_PRODUCE_ADD_IMAGE)}
            </MenuItem>
          </Menu>
          {hasLayers ? (
            <SortableNestedLayerList
              nodes={nodes}
              nodeLayouts={nodeLayouts}
              itemsById={itemsById}
              selectedID={selectedID}
              selectedIDs={selectedIDs}
              expandedIDs={
                selectedParentID
                  ? { ...expandedIDs, [selectedParentID]: true }
                  : expandedIDs
              }
              onSelect={onSelectLayer}
              onRename={onRenameLayer}
              onToggleExpand={onToggleExpand}
              onReorder={onReorderSiblings}
              onDelete={onDeleteLayer}
              onCopy={onCopyLayer}
              fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                index: 1,
              })}
              deleteLabel={t(TEMPLATES_DELETE)}
              copyLabel={t(TEMPLATES_DUPLICATE)}
              lockLabel={t(TEMPLATES_LOCK_LAYER)}
              unlockLabel={t(TEMPLATES_UNLOCK_LAYER)}
              hideLabel={t(TEMPLATES_HIDE_LAYER)}
              showLabel={t(TEMPLATES_SHOW_LAYER)}
              onToggleLock={onToggleLock}
              onToggleVisible={onToggleVisible}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minHeight: 160,
              }}
            >
              <Box sx={{ textAlign: "center", maxWidth: 320 }}>
                <Box
                  sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: "#000",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Layers size={24} color="#fff" />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {isAssetsGalleryOpen ? (
        <ModalGalleryAssets
          modalOpen={isAssetsGalleryOpen}
          multiple
          onCloseModal={() => setIsAssetsGalleryOpen(false)}
          onCallbackSelectedItems={onSelectGalleryItems}
        />
      ) : null}
    </Box>
  );
};

export default InstanceLayersSidebar;
